import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone } from "lucide-react";
import { useLang } from "@/i18n";
import { LOGO } from "@/assets";
import SubscribeForm from "@/components/SubscribeForm";

export const Footer = () => {
  const { t } = useLang();
  const cols = [
    { title: t("nav_history"), links: [["/histoire", t("nav_history")], ["/frise", t("nav_frise")], ["/bibliographie", t("nav_bibliography")], ["/chronologie", t("nav_timeline")], ["/figures", t("nav_figures")]] },
    { title: t("nav_peoples"), links: [["/peuples", t("nav_peoples")], ["/culture", t("nav_culture")], ["/contes", t("nav_tales")]] },
    { title: t("nav_projects"), links: [["/projets", t("nav_projects")], ["/evenements", t("nav_events")], ["/galeries", t("nav_galleries")]] },
    { title: t("nav_about"), links: [["/a-propos", t("nav_about")], ["/gouvernance", "Gouvernance"], ["/organigramme", t("nav_org")], ["/contact", "Contact"]] },
  ];
  return (
    <footer className="relative bg-ab-ebony mt-0">
      <div className="absolute top-0 left-0 right-0 ab-hairline-gold" aria-hidden="true" />
      <div className="ab-container py-16 grid grid-cols-2 md:grid-cols-5 gap-10">
        <div className="col-span-2">
          <div className="flex items-center gap-3 mb-3">
            <img src={LOGO} alt="Anyang Beti" className="w-12 h-12 object-contain rounded-full ring-1 ring-ab-gold/40" />
            <div className="font-serif text-2xl text-ab-ivory">
              ANYANG <span className="text-ab-gold">BETI</span>
            </div>
          </div>
          <p className="text-ab-ivory/60 text-sm max-w-xs leading-relaxed">{t("subtitle")}</p>
          <p className="mt-6 text-ab-gold font-serif italic text-lg">{t("signature")}</p>
          <p className="mt-2 text-ab-ivory/40 text-xs tracking-widest uppercase">{t("devise")}</p>
          <div className="mt-6 space-y-1.5 text-sm" data-testid="footer-contact">
            <a href="mailto:anyangbeti5@gmail.com" className="flex items-center gap-2 text-ab-ivory/60 hover:text-ab-gold transition-colors"><Mail size={14} className="text-ab-gold" /> anyangbeti5@gmail.com</a>
            <a href="tel:+237655595895" className="flex items-center gap-2 text-ab-ivory/60 hover:text-ab-gold transition-colors"><Phone size={14} className="text-ab-gold" /> +237 6 55 59 58 95</a>
            <a href="tel:+237653602707" className="flex items-center gap-2 text-ab-ivory/60 hover:text-ab-gold transition-colors"><Phone size={14} className="text-ab-gold" /> +237 6 53 60 27 07</a>
          </div>
        </div>
        {cols.map((c) => (
          <div key={c.title}>
            <h4 className="text-ab-gold text-xs tracking-widest uppercase mb-4">{c.title}</h4>
            <ul className="space-y-2">
              {c.links.map(([to, label]) => (
                <li key={to}>
                  <Link
                    to={to}
                    data-testid={to === "/frise" ? "footer-frise-link" : to === "/bibliographie" ? "footer-biblio-link" : undefined}
                    className="text-ab-ivory/60 hover:text-ab-ivory text-sm transition-colors"
                  >{label}</Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-ab-gold/10">
        <div className="ab-container py-10">
          <div className="max-w-xl">
            <SubscribeForm type="newsletter" variant="footer" />
          </div>
        </div>
      </div>
      <div className="border-t border-ab-gold/10 relative z-10" data-testid="footer-devise">
        <div className="ab-container py-12 text-center">
          <div className="flex items-center justify-center gap-4 mb-4">
            <span className="h-px w-12 sm:w-24 bg-gradient-to-r from-transparent to-ab-gold/50" />
            <span className="text-ab-gold text-xl">✦</span>
            <span className="h-px w-12 sm:w-24 bg-gradient-to-l from-transparent to-ab-gold/50" />
          </div>
          <p className="font-serif italic text-2xl md:text-3xl text-ab-gold leading-snug">« {t("signature")} »</p>
          <p className="mt-4 text-ab-ivory/50 text-xs md:text-sm tracking-[0.3em] uppercase">{t("devise")}</p>
        </div>
      </div>
      <div className="border-t border-ab-gold/10 relative z-10">
        <div className="ab-container py-6 flex flex-col md:flex-row justify-between gap-3 text-xs text-ab-ivory/40">
          <span>© {new Date().getFullYear()} ANYANG BETI — Mémoire numérique du peuple Beti.</span>
          <div className="flex gap-5">
            <Link to="/a-propos" className="hover:text-ab-gold">Confidentialité</Link>
            <Link to="/a-propos" className="hover:text-ab-gold">Conditions</Link>
            <Link to="/a-propos" className="hover:text-ab-gold">Charte des contributions</Link>
          </div>
        </div>
        <div className="ab-container pb-6 -mt-3">
          <p className="text-[0.65rem] text-ab-ivory/30">
            Ambiance sonore : tam-tam ewondo (nkul, tambour à fente), via{" "}
            <a href="https://commons.wikimedia.org/wiki/File:Satch_Hoyt,_Slit_Drum,_Br%C3%BCcke-Museum_Berlin,_65053.mp3" target="_blank" rel="noopener noreferrer" className="hover:text-ab-gold underline">
              Wikimedia Commons
            </a>{" "}
            — licence CC BY-SA 4.0.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
